import { TrackService } from '@aiwyn/shared';
import { inject, Injectable } from '@angular/core';
import { EngagementEvent, EngagementOpenedEvent } from '../models';

/**
 * Thin wrapper around `TrackService` for the two engagement-page-open events. These sit
 * outside the workflow taxonomy on purpose (no funnel steps), so they go through raw
 * `trackService.track(...)` instead of `trackWorkflow(...)`.
 *
 * Identity (tenant / contact / client) is attached centrally by adapters via
 * `TrackService.identify(TrackingIdentity)`, so call sites never re-stamp it on metadata.
 *
 * The only state here is the per-tab dedupe set for ENGAGEMENT_OPENED (see field doc).
 */
@Injectable({ providedIn: 'root' })
export class EngagementTrackingService {
  private readonly trackService = inject(TrackService);

  /**
   * Engagement IDs we've already fired ENGAGEMENT_OPENED for in this tab session.
   *
   * Dedup is per session, not per route navigation: opening engagement #42, navigating away,
   * and coming back fires once. The set lives for the lifetime of this providedIn:root service
   * (i.e. tab lifetime). We intentionally don't reset on logout — same-tab logout/login is rare
   * and the worst-case impact is one missed event.
   */
  private readonly firedEngagementIds = new Set<number>();

  trackEngagementOpened(engagementId: number): void {
    if (this.firedEngagementIds.has(engagementId)) {
      return;
    }

    this.firedEngagementIds.add(engagementId);

    this.trackService.track(EngagementEvent.ENGAGEMENT_OPENED, {
      engagementId,
    } satisfies EngagementOpenedEvent);
  }

  trackEngagementListOpened(): void {
    this.trackService.track(EngagementEvent.ENGAGEMENT_LIST_OPENED);
  }
}
